/**
 * Table body for the PO Line Dashboard drill-down modal.
 *
 * Extracted from ``POLineDrilldownModal`` (500-line limit) and shaped like the
 * Scheduled Dashboard's ``CurrentWeekDrilldownTable``: the two judging dates
 * lead every row (Delivery/Due vs Receipt), verdicts render as coloured pills,
 * PO # / Line deep-link, and line rows grow an OTIF decision column when the
 * server says the requesting user may act (``can_*`` flags — admins only).
 */
import { ArrowUp, ArrowDown, ArrowUpDown, ShieldCheck } from 'lucide-react';
import PoLineLink from '../../common/PoLineLink';
import PoHeaderLink from '../../common/PoHeaderLink';
import { Badge } from '../../ui/badge';
import OTIFReviewActions from '../../purchasing/OTIFReviewActions';
import type {
  POLineDrilldownAppealRow,
  POLineDrilldownEntity,
  POLineDrilldownGrnRow,
  POLineDrilldownLineRow,
  POLineDrilldownNcrRow,
  POLineDrilldownRow,
} from '../../../lib/api/po-line-dashboard';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { COLUMNS_BY_ENTITY } from './poLineDrilldownModes';

const LINE_STATUS: Record<string, string> = {
  // CR-7412: a finished line reads Closed here too.
  pending: 'Pending', partially_received: 'Partially received', received: 'Closed', cancelled: 'Cancelled',
};

/** PO-line OTIF verdict pill — the cached verdict the tile counted.
 *
 *  This states the VERDICT (OTIF / Early / Late / Short / Arrear / Pending)
 *  with the review folded in, which is a different question from the OTIF
 *  Review column; it keeps its own vocabulary and only borrows the colour. */
export function LineOtifPill({ row }: { row: POLineDrilldownLineRow }) {
  if (row.otif_overridden) {
    // CR-7418 (Aaron, 19 Sep): overridden is TEAL across the portal. Emerald is
    // the natural-pass colour, and this pill sits a click from the modals that
    // now render teal for the same fact.
    return (
      <Badge variant="outline" className="bg-teal-100 text-teal-800 border-teal-300 whitespace-nowrap" title="Manual OTIF override">
        <ShieldCheck className="h-3 w-3 mr-1" />Passed (Override)
      </Badge>
    );
  }
  const map: Record<string, { label: string; cls: string }> = {
    otif:    { label: 'OTIF',    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    early:   { label: 'Early',   cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    late:    { label: 'Late',    cls: 'bg-red-100 text-red-900 border-red-300' },
    short:   { label: 'Short',   cls: 'bg-amber-50 text-amber-800 border-amber-200' },
    arrear:  { label: 'Arrear',  cls: 'bg-red-50 text-red-700 border-red-200' },
    pending: { label: 'Pending', cls: 'bg-slate-50 text-slate-600 border-slate-200' },
  };
  // CR-7270: no verdict is plain text, not a badge — a badge is emphasis, and
  // there is nothing to emphasise about a verdict that does not exist (an
  // arrears member was not judged at the boundary). REWORK (Aaron, 18 Sep):
  // the word is "Pending", which this pill's own map already uses for a line
  // that is not yet due, and which the other two drill-downs now print for the
  // same state — one vocabulary across all three.
  if (!row.otif_status) return <span className="text-muted-foreground">Pending</span>;
  const ack = row.otif_acknowledged ? ' (Acknowledged)' : '';
  // CR-8673: a judged line is either PASSED or FAILED, the words the PO Lines
  // grid and the OTIF Analysis drill-down print (CR-7416). Why it failed
  // (early / late / short) stays in the tooltip and on each GRN's timing pill.
  if (row.is_otif === true || row.is_otif === false) {
    const passed = row.is_otif === true;
    const reason = map[row.otif_status]?.label ?? row.otif_status;
    const title = passed ? 'Every delivery on time and in full'
      : `Failed: ${reason}${ack ? ' - fail reviewed and acknowledged' : ''}`;
    return (
      <Badge variant="outline" className={`${passed ? PASS_CLS : FAIL_CLS} whitespace-nowrap`} title={title}>
        {passed ? 'PASSED' : 'FAILED'}{ack}
      </Badge>
    );
  }
  // No verdict yet: the open line's own state (Pending / Arrear / Short).
  const m = map[row.otif_status] ?? { label: row.otif_status, cls: 'bg-slate-50 text-slate-600 border-slate-200' };
  return <Badge variant="outline" className={`${m.cls} whitespace-nowrap`}>{m.label}</Badge>;
}

/** CR-8673: the PASSED / FAILED pill colours (the pass and the late badge's). */
const PASS_CLS = 'bg-emerald-50 text-emerald-700 border-emerald-200';
const FAIL_CLS = 'bg-red-100 text-red-900 border-red-300';

/** GRN on-time verdict pill (receipt vs due date). */
export function TimingPill({ value }: { value: string | null }) {
  if (!value) return <span className="text-muted-foreground">-</span>;
  const map: Record<string, { label: string; cls: string }> = {
    on_time: { label: 'On time', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    overridden_early: { label: 'Overridden (Early)', cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
    overridden_late: { label: 'Overridden (Late)', cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
    early: { label: 'Early', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    late: { label: 'Late', cls: 'bg-red-100 text-red-900 border-red-300' },
  };
  const m = map[value] ?? { label: value, cls: 'bg-slate-50 text-slate-600 border-slate-200' };
  return <Badge variant="outline" className={`${m.cls} whitespace-nowrap`}>{m.label}</Badge>;
}

/**
 * Mute zero numeric values so the eye lands on the non-zero rows.
 *
 * CR-7270: an ABSENT value is not zero. On a frozen row a null quantity means
 * the month never recorded it (a row captured before the wide snapshot), and
 * printing 0 there states "nothing was ordered / received", which the capture
 * cannot support. `absent` is what the cell says instead — '-', the one mark
 * this grid and its two sibling drill-downs use for a value that is not there
 * (Aaron, 18 Sep 2026).
 */
function NumCell({ value, format, absent = '-' }: { value: number | null | undefined; format: (n: number) => string; absent?: string }) {
  if (value === null || value === undefined) return <span className="text-muted-foreground">{absent}</span>;
  if (!value) return <span className="text-muted-foreground">0</span>;
  return <span className="tabular-nums">{format(value)}</span>;
}

interface POLineDrilldownTableProps {
  entity: POLineDrilldownEntity;
  rows: POLineDrilldownRow[];
  ordering: string;
  onHeaderClick: (sortKey: string | null) => void;
  currency: string;
  /** Called after a row-level OTIF decision succeeds so the caller refetches. */
  onOtifDecision?: () => void;
}

export function POLineDrilldownTable({ entity, rows, ordering, onHeaderClick, currency, onOtifDecision }: POLineDrilldownTableProps) {
  const { formatCurrency, formatDate, formatNumber } = useFormatters();
  const columns = COLUMNS_BY_ENTITY[entity];
  const lineRows = entity === 'line' ? (rows as POLineDrilldownLineRow[]) : [];
  const showActions = entity === 'line' && !!onOtifDecision
    && lineRows.some((l) => l.can_override_otif || l.can_acknowledge_otif || l.can_reset_otif);

  // CR-7270 rework (Aaron, 18 Sep): one absent mark everywhere — '-' on a live
  // row and a frozen one alike, and the same character the other two
  // drill-downs use (this file printed an em dash, they print a hyphen).
  const absentFor = (_row: POLineDrilldownRow) => '-';
  const dateOrDash = (v: string | null | undefined, _row?: POLineDrilldownRow) => (v ? formatDate(v) : '-');
  const sortIcon = (sortKey: string | null) => {
    if (!sortKey) return null;
    if (ordering === sortKey) return <ArrowUp className="inline h-3 w-3 ml-1 text-primary" />;
    if (ordering === `-${sortKey}`) return <ArrowDown className="inline h-3 w-3 ml-1 text-primary" />;
    return <ArrowUpDown className="inline h-3 w-3 ml-1 text-muted-foreground" />;
  };

  const renderRow = (r: POLineDrilldownRow) => {
    if (entity === 'line') {
      const l = r as POLineDrilldownLineRow;
      return (
        <tr key={l.id} className="hover:bg-slate-50">
          <td className="px-3 py-2 whitespace-nowrap">{dateOrDash(l.odd_date ?? l.delivery_date, l)}</td>
          <td className="px-3 py-2 whitespace-nowrap">{dateOrDash(l.delivery_date, l)}</td>
          <td className="px-3 py-2 whitespace-nowrap">{dateOrDash(l.last_receipt_date, l)}</td>
          <td className="px-3 py-2 whitespace-nowrap">{dateOrDash(l.supplier_promise_date, l)}</td>
          <td className="px-3 py-2 font-mono text-xs whitespace-nowrap"><PoHeaderLink poLineId={l.id} poNumber={l.po_number}>{l.po_number}</PoHeaderLink></td>
          <td className="px-3 py-2 text-right whitespace-nowrap"><PoLineLink poLineId={l.id}><NumCell value={l.line_number} format={String} /></PoLineLink></td>
          <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{l.part_number}</td>
          <td className="px-3 py-2 text-xs max-w-[280px] truncate" title={l.part_description || ''}>{l.part_description || '-'}</td>
          <td className="px-3 py-2 text-xs text-muted-foreground max-w-[180px] truncate" title={l.supplier_name || ''}>{l.supplier_name || '-'}</td>
          <td className="px-3 py-2 text-right whitespace-nowrap"><NumCell value={l.quantity} format={formatNumber} absent={absentFor(l)} /></td>
          <td className="px-3 py-2 text-right whitespace-nowrap"><NumCell value={l.received_qty} format={formatNumber} absent={absentFor(l)} /></td>
          <td className="px-3 py-2 text-right whitespace-nowrap"><NumCell value={l.outstanding_qty} format={formatNumber} absent={absentFor(l)} /></td>
          <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">{l.line_value == null ? <span className="text-muted-foreground">{absentFor(l)}</span> : formatCurrency(l.line_value, currency)}</td>
          <td className="px-3 py-2 text-xs whitespace-nowrap">{l.status ? <>{LINE_STATUS[l.status] ?? l.status}{l.on_hold ? ' · On hold' : ''}</> : <span className="text-muted-foreground">{absentFor(l)}</span>}</td>
          <td className="px-3 py-2 whitespace-nowrap"><LineOtifPill row={l} /></td>
          {showActions && (
            <td className="px-3 py-2 whitespace-nowrap">
              <OTIFReviewActions line={l} onDone={onOtifDecision ?? (() => undefined)} />
            </td>
          )}
        </tr>
      );
    }
    if (entity === 'grn') {
      const g = r as POLineDrilldownGrnRow;
      return (
        <tr key={g.id} className="hover:bg-slate-50">
          <td className="px-3 py-2 whitespace-nowrap">{dateOrDash(g.receipt_date)}</td>
          <td className="px-3 py-2 whitespace-nowrap">{dateOrDash(g.odd_date ?? g.delivery_date)}</td>
          <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{g.grn_number}</td>
          <td className="px-3 py-2 font-mono text-xs whitespace-nowrap"><PoHeaderLink poLineId={g.po_line_id} poNumber={g.po_number}>{g.po_number}</PoHeaderLink></td>
          <td className="px-3 py-2 text-right whitespace-nowrap"><PoLineLink poLineId={g.po_line_id}><NumCell value={g.line_number} format={String} /></PoLineLink></td>
          <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{g.part_number}</td>
          <td className="px-3 py-2 text-xs max-w-[280px] truncate" title={g.part_description || ''}>{g.part_description || '-'}</td>
          <td className="px-3 py-2 text-xs text-muted-foreground max-w-[180px] truncate" title={g.supplier_name || ''}>{g.supplier_name || '-'}</td>
          <td className="px-3 py-2 text-right whitespace-nowrap"><NumCell value={g.ordered_qty} format={formatNumber} /></td>
          <td className="px-3 py-2 text-right whitespace-nowrap"><NumCell value={g.quantity_received} format={formatNumber} /></td>
          <td className="px-3 py-2 whitespace-nowrap"><TimingPill value={g.on_time_status} /></td>
        </tr>
      );
    }
    if (entity === 'ncr') {
      const n = r as POLineDrilldownNcrRow;
      return (
        <tr key={n.id} className="hover:bg-slate-50">
          <td className="px-3 py-2 whitespace-nowrap">{dateOrDash(n.ncr_date)}</td>
          <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{n.ncr_number || '-'}</td>
          <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{n.part_number}</td>
          <td className="px-3 py-2 text-xs max-w-[280px] truncate" title={n.part_description || ''}>{n.part_description || '-'}</td>
          <td className="px-3 py-2 text-xs text-muted-foreground max-w-[180px] truncate" title={n.supplier_name || ''}>{n.supplier_name || '-'}</td>
          <td className="px-3 py-2 text-right whitespace-nowrap"><NumCell value={n.reject_qty} format={formatNumber} /></td>
          <td className="px-3 py-2 text-xs whitespace-nowrap">{n.status}</td>
        </tr>
      );
    }
    const a = r as POLineDrilldownAppealRow;
    return (
      <tr key={a.id} className="hover:bg-slate-50">
        <td className="px-3 py-2 whitespace-nowrap">{dateOrDash(a.created_at)}</td>
        <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{a.grn_number || '-'}</td>
        <td className="px-3 py-2 font-mono text-xs whitespace-nowrap"><PoHeaderLink poLineId={a.po_line_id} poNumber={a.po_number}>{a.po_number || '-'}</PoHeaderLink></td>
        <td className="px-3 py-2 text-right whitespace-nowrap"><PoLineLink poLineId={a.po_line_id}>{a.line_number ?? '-'}</PoLineLink></td>
        <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{a.part_number || '-'}</td>
        <td className="px-3 py-2 text-xs text-muted-foreground max-w-[180px] truncate" title={a.supplier_name || ''}>{a.supplier_name || '-'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{a.qty_claimed == null ? '-' : <NumCell value={a.qty_claimed} format={formatNumber} />}</td>
        <td className="px-3 py-2 text-xs whitespace-nowrap">{a.status}</td>
      </tr>
    );
  };

  return (
    <div className="overflow-auto max-h-[60vh] border rounded-md">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 sticky top-0 z-10">
          <tr>
            {columns.map((c) => {
              const align = c.align === 'right' ? 'text-right' : 'text-left';
              const cursor = c.sort ? 'cursor-pointer hover:bg-slate-100' : '';
              const sorted = c.sort && (ordering === c.sort || ordering === `-${c.sort}`);
              return (
                <th
                  key={c.id}
                  title={c.hint}
                  className={`px-3 py-2 font-medium text-slate-700 select-none whitespace-nowrap ${align} ${cursor}`}
                  onClick={() => onHeaderClick(c.sort)}
                  aria-sort={sorted ? (ordering.startsWith('-') ? 'descending' : 'ascending') : undefined}
                >
                  {c.label}{sortIcon(c.sort)}
                </th>
              );
            })}
            {/* ACTIONS, not the status. "OTIF Review" is the status column
                everywhere else in the portal after CR-7418, so naming the
                button column that too would give one label two meanings. */}
            {showActions && (
              <th className="px-3 py-2 font-medium text-slate-700 text-left whitespace-nowrap" title="Admin: override, acknowledge or reset this line's OTIF review">
                OTIF Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{rows.map(renderRow)}</tbody>
      </table>
    </div>
  );
}
